import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ResultPatternView } from '@lp/contracts';
import { ArrowLeft, Pencil, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { errorText, fieldProblems, get, patch, post } from '@/lib/api';
import { useIsSuperAdmin } from '@/lib/auth';
import { useT } from '@/lib/i18n';
import { Badge, Button, Card, Dialog, ErrorBox, Field, Input, Loading, PageHeader, Select } from '@/components/ui';

/** the result pattern library: what each pattern reads, what it pays, who uses it */
export function PatternsPage() {
  const t = useT();
  const list = useQuery({ queryKey: ['patterns'], queryFn: () => get<ResultPatternView[]>('/platform/patterns') });
  const superAdmin = useIsSuperAdmin();
  const [editing, setEditing] = useState<ResultPatternView | 'new' | null>(null);
  return (
    <>
      <Link to="/products" className="mb-2 inline-flex items-center gap-1 text-xs text-ink-500 hover:text-ink-800">
        <ArrowLeft size={12} /> {t('Products')}
      </Link>
      <PageHeader
        title={t('Result patterns')}
        subtitle={t('How a draw’s result sheet becomes the result of each post. A product’s game names its pattern; a post no pattern produces is typed by hand as its 2D and 3D number.')}
        actions={
          superAdmin && (
            <Button icon={<Plus size={14} />} onClick={() => setEditing('new')}>
              {t('New pattern')}
            </Button>
          )
        }
      />
      {editing && list.data && <PatternDialog pattern={editing === 'new' ? null : editing} all={list.data} onClose={() => setEditing(null)} />}
      {list.isLoading ? (
        <Loading />
      ) : list.error ? (
        <ErrorBox text={errorText(list.error)} />
      ) : (
        <div className="space-y-4">
          {list.data!.map((p) => (
            <Card
              key={p.code}
              title={
                <span>
                  {p.nameEn} <span className="font-mono text-xs text-ink-500">{p.code}</span>
                </span>
              }
              actions={
                <span className="flex items-center gap-2">
                  {p.origin === 'custom' && <Badge tone="blue">{t('Own pattern')}</Badge>}
                  {p.usedBy.length ? <Badge tone="green">{t('In use')}</Badge> : <Badge>{t('Not used yet')}</Badge>}
                  {superAdmin && (
                    <Button variant="ghost" icon={<Pencil size={12} />} onClick={() => setEditing(p)}>
                      {t('Edit')}
                    </Button>
                  )}
                </span>
              }
            >
              <div data-testid={`pattern-${p.code}`} className="space-y-3 text-sm">
                <p className="text-xs text-ink-500">{p.nameKm}</p>
                {p.description && <p className="text-ink-700">{p.description}</p>}
                <div>
                  <div className="mb-1 text-xs font-medium text-ink-500">{t('Sheets it reads')}</div>
                  <ul className="space-y-1">
                    {p.roles.map((r) => (
                      <li key={r.role}>
                        <span className="font-mono font-semibold">{r.role}</span> <span className="text-ink-600">{r.label}</span>
                        <span className="ml-2 text-xs text-ink-500">
                          {r.slots.map((s) => `${s.label ?? s.key} ${s.count}×${s.digits}`).join(' · ')}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {p.aliases.length > 0 && (
                    <p className="mt-1 text-xs text-ink-500">
                      {t('Chosen by weekday')}: {p.aliases.map((a) => `${a.role} = ${Object.entries(a.byWeekday).map(([d, r]) => `${d} ${r}`).join(', ')}`).join(' · ')}
                    </p>
                  )}
                </div>
                <div>
                  <div className="mb-1 text-xs font-medium text-ink-500">{t('Posts it pays')}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {p.matching.map((m, i) => (
                      <span key={i} className="rounded border border-ink-200 px-1.5 py-0.5 font-mono text-xs">
                        {m.post} {m.digits.map((d) => `${d}D`).join('/')}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="text-xs text-ink-600">
                  {t('Used by')}: {p.usedBy.length ? p.usedBy.map((u) => `${u.productCode} v${u.version} ${u.gameCode}`).join(' · ') : '—'}
                </div>
                {p.notes.length > 0 && <ul className="list-disc pl-5 text-xs text-ink-500">{p.notes.map((n, i) => <li key={i}>{n}</li>)}</ul>}
              </div>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}

/**
 * a new pattern (a copy of one, or a rule written in the engine's rule language) — or one changed: its names always; its rule only when it
 * is the company's own and no published product uses it (a used rule never changes under a result)
 */
function PatternDialog({ pattern, all, onClose }: { pattern: ResultPatternView | null; all: ResultPatternView[]; onClose: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const ruleEditable = !pattern || (pattern.origin === 'custom' && !pattern.usedBy.length);
  const [f, setF] = useState({ code: '', nameEn: pattern?.nameEn ?? '', nameKm: pattern?.nameKm ?? '', description: pattern?.description ?? '', copyOf: pattern ? '' : (all[0]?.code ?? ''), dsl: pattern ? JSON.stringify(pattern.dsl, null, 2) : '' });
  const [dslProblem, setDslProblem] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => {
      let dsl: unknown;
      if (f.dsl.trim() && (!pattern || ruleEditable)) {
        try {
          dsl = JSON.parse(f.dsl);
        } catch (e) {
          setDslProblem((e as Error).message);
          throw e;
        }
      }
      setDslProblem(null);
      if (!pattern) return post<ResultPatternView>('/platform/patterns', { code: f.code, nameEn: f.nameEn, nameKm: f.nameKm, ...(f.description.trim() ? { description: f.description.trim() } : {}), ...(dsl !== undefined ? { dsl } : { copyOf: f.copyOf }) });
      const changedDsl = ruleEditable && dsl !== undefined && JSON.stringify(dsl) !== JSON.stringify(pattern.dsl);
      return patch<ResultPatternView>(`/platform/patterns/${pattern.code}`, { nameEn: f.nameEn, nameKm: f.nameKm, description: f.description.trim() || null, ...(changedDsl ? { dsl } : {}) });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['patterns'] });
      onClose();
    },
  });
  return (
    <Dialog
      title={pattern ? `${t('Edit')} — ${pattern.code}` : t('New pattern')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => save.mutate()} disabled={(!pattern && !f.code) || !f.nameEn.trim() || !f.nameKm.trim() || save.isPending}>
            {t('Save')}
          </Button>
        </>
      }
    >
      {!pattern && (
        <Field label={t('Code')}>
          <Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toLowerCase() })} maxLength={32} placeholder="tn-south" />
        </Field>
      )}
      <Field label={t('Name (English)')}>
        <Input value={f.nameEn} onChange={(e) => setF({ ...f, nameEn: e.target.value })} maxLength={80} />
      </Field>
      <Field label={t('Name (Khmer)')}>
        <Input value={f.nameKm} onChange={(e) => setF({ ...f, nameKm: e.target.value })} maxLength={80} />
      </Field>
      <Field label={t('Description')}>
        <Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} maxLength={400} />
      </Field>
      {!pattern && (
        <Field label={t('Start as a copy of')}>
          <Select value={f.copyOf} onChange={(e) => setF({ ...f, copyOf: e.target.value })} className="w-full" disabled={!!f.dsl.trim()}>
            {all.map((p) => (
              <option key={p.code} value={p.code}>
                {p.nameEn} ({p.code})
              </option>
            ))}
          </Select>
        </Field>
      )}
      <Field label={t('Rule (JSON)')}>
        <textarea
          value={f.dsl}
          onChange={(e) => setF({ ...f, dsl: e.target.value })}
          readOnly={!ruleEditable}
          rows={10}
          className="w-full rounded-md border border-ink-200 p-2 font-mono text-[11px]"
          placeholder={pattern ? '' : t('Empty = the copy above')}
          aria-label={t('Rule (JSON)')}
        />
      </Field>
      {!ruleEditable && <p className="text-xs text-amber-700">{t(pattern?.origin === 'builtin' ? 'A pattern from the engine is not edited — make a copy.' : 'A published product uses this pattern — make a copy and use the copy in a new version.')}</p>}
      {dslProblem && <ErrorBox text={dslProblem} />}
      {save.error && !dslProblem && <ErrorBox text={[errorText(save.error), ...fieldProblems(save.error)].join(' — ')} />}
    </Dialog>
  );
}
